import React from "react";
import { useLazyFetchReportTemplatesQuery } from "../../../services/clientreports";

const ReportTemplateContext = React.createContext();

const useReportTemplatesData = () => React.useContext(ReportTemplateContext);

export const ReportTemplateProvider = ({ children }) => {
    const [sort, setSort] = React.useState("CreatedDate+desc");
    const [pageLimit, setPageLimit] = React.useState(50);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [load, { data, isLoading, isFetching, ...loadResults }] = useLazyFetchReportTemplatesQuery();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const clear = () => {
        if (isNextPageLoading) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        setIsLoadingState(_ => true);
        load({
            cacheKey: newCacheKey,
            searchText: searchTerm,
            sort,
            page: 1,
            limit: pageLimit
        }).unwrap().then(_ => {
            setCacheKey(_prev => newCacheKey);
            setIsLoadingState(_prev => false);
        });
    };

    const isRowLoaded = (index) => !hasMore || index < results.length;

    const loadMore = () => {
        // safety check to make sure we don't reload when already loading!
        if (isNextPageLoading) {
            return;
        }

        setIsLoadingState(_ => true);
        load({ 
            cacheKey, 
            searchText: searchTerm,
            sort,
            page: page + 1, 
            limit: pageLimit,   
        }).unwrap().then(_ => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property) => {
        // we want the sort to initially be descending ??
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
        clear();
    };

    React.useEffect(clear, [ searchTerm ]);

    return (
        <ReportTemplateContext.Provider value={{
            ...loadResults,
            clear,
            data: results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore, 
            newRecordsSinceCache,
            page,
            pagination,
            searchTerm,
            setSearchTerm,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            {children}
        </ReportTemplateContext.Provider>
    );
};

export default useReportTemplatesData;